import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../assets/icons/index';
import Button from '../../../components/button/index';
import { Checkbox, FormCheckbox } from '../../../components/checkbox/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../components/combobox/index';
import { FormInput } from '../../../components/input/index';
import { Sheet, SheetContent } from '../../../components/sheet/index';
import { Switch } from '../../../components/switch/index';
import { HttpException } from '../../../exceptions/index';
import { CompanyHooks, useReactForm } from '../../../hooks/index';
import { SubscriptionOffer } from '../../../types/offer.types';
import {
  SubscriptionDuration,
  SubscriptionType,
  subscriptionTypeTitle,
} from '../../../types/plan.types';
import {
  CreateOfferFormSchema,
  createOfferSchema,
  parseRecipients,
} from './create-subscription-modal.validation';

export type SubscriptionModalMode = 'create' | 'edit' | 'view';

export type CreateSubscriptionModalProps = {
  handleClose: () => void;
  invalidateQuery: () => void;
  isOpenModal: boolean;
  mode?: SubscriptionModalMode;
  offer?: null | SubscriptionOffer;
  onSwitchToEdit?: () => void;
};

const ENVELOPE_HINTS: Partial<Record<SubscriptionType, string>> = {
  [SubscriptionType.GROWTH]: 'Default amount for Growth plan is 200',
  [SubscriptionType.START_UP]: 'Default amount for Startup plan is 100',
};

const MODAL_TITLE: Record<SubscriptionModalMode, string> = {
  create: 'Create Subscription',
  edit: 'Edit Subscription',
  view: 'View Subscription',
};

const CREATE_DEFAULT_VALUES: Partial<CreateOfferFormSchema> = {
  planType: SubscriptionType.ENTERPRISE,
  currency: 'USD',
  docusignEnabled: false,
  monthlyEnabled: false,
  recipients: '',
  promoCode: '',
};

const INVALID_PROMO_CODE_MESSAGE = 'This promocode is not valid';

const isInvalidPromoCodeError = (error: unknown) =>
  error instanceof HttpException && error.body?.message === INVALID_PROMO_CODE_MESSAGE;

const offerToFormValues = (offer: SubscriptionOffer): Partial<CreateOfferFormSchema> => ({
  companyName: offer.companyName,
  companyDomain: offer.companyDomain,
  planType: offer.planType,
  currency: 'USD',
  stakeholderLimit: offer.stakeholdersLimit,
  docusignEnabled: offer.docusignEnvelopeLimit != null,
  docusignEnvelopeLimit: offer.docusignEnvelopeLimit ?? undefined,
  monthlyEnabled: offer.isMonthlyEnabled,
  monthlyPrice: offer.monthlyPriceInCents != null ? offer.monthlyPriceInCents / 100 : undefined,
  yearlyPrice: offer.yearlyPriceInCents / 100,
  recipients: offer.recipients.join(', '),
  promoCode: offer.promoCode ?? '',
});

export const CreateSubscriptionModal: FC<CreateSubscriptionModalProps> = ({
  isOpenModal,
  handleClose,
  invalidateQuery,
  mode = 'create',
  offer,
  onSwitchToEdit,
}) => {
  const { createSubscriptionOfferAsync, isPending: isCreating } =
    CompanyHooks.useCreateSubscriptionOffer();
  const { updateSubscriptionOfferAsync, isPending: isUpdating } =
    CompanyHooks.useUpdateSubscriptionOffer();

  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isPending = isCreating || isUpdating;

  const { plans } = CompanyHooks.useBasicPlans({ enabled: isOpenModal && !isView });

  const { control, handleSubmit, reset, setValue, getValues, setError, clearErrors } = useReactForm(
    {
      schema: createOfferSchema,
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: CREATE_DEFAULT_VALUES,
    },
  );

  const formValues = useWatch<CreateOfferFormSchema>({ control });
  const { planType, docusignEnabled, monthlyEnabled, recipients } = formValues;

  const isEnterprise = planType === SubscriptionType.ENTERPRISE;
  const hasRecipient = parseRecipients(recipients).length > 0;

  // The form validates on submit only (mode: 'onSubmit'), so formState.isValid is not live.
  // Gate the create buttons on a silent schema check instead — no inline errors until submit.
  const isFormValid = useMemo(() => createOfferSchema.safeParse(formValues).success, [formValues]);

  // Prefill when opening in edit/view; reset to blank defaults for a fresh create.
  useEffect(() => {
    if (!isOpenModal) return;

    if (offer && mode !== 'create') {
      reset(offerToFormValues(offer));
    } else if (mode === 'create') {
      reset(CREATE_DEFAULT_VALUES);
    }
  }, [isOpenModal, offer, mode, reset]);

  // Write only real changes — a redundant setValue re-notifies useWatch and would re-run the
  // plan-sync effects every render (React's max-update-depth crash).
  const setFieldIfChanged = useCallback(
    (name: 'monthlyPrice' | 'stakeholderLimit' | 'yearlyPrice', value: number | undefined) => {
      if (getValues(name) !== value) setValue(name, value);
    },
    [getValues, setValue],
  );

  // Standard plans show the system prices and preset stakeholder limit (read-only inputs) — keep
  // them in sync with the catalog, including when the plans query resolves after the modal opened.
  useEffect(() => {
    if (!isOpenModal || isView || !planType || planType === SubscriptionType.ENTERPRISE) return;

    const systemPlan = (duration: SubscriptionDuration) =>
      plans.find((plan) => plan.type === planType && plan.duration === duration);

    const monthlyPlan = systemPlan(SubscriptionDuration.MONTHLY);
    const yearlyPlan = systemPlan(SubscriptionDuration.ANNUALY);

    // Assign even when the catalog has no entry (e.g. plans not synced yet) so a previously
    // entered Enterprise value is never shown as a standard-plan value.
    setFieldIfChanged('monthlyPrice', monthlyPlan ? monthlyPlan.priceInCents / 100 : undefined);
    setFieldIfChanged('yearlyPrice', yearlyPlan ? yearlyPlan.priceInCents / 100 : undefined);
    setFieldIfChanged('stakeholderLimit', yearlyPlan?.stakeholdersCount || undefined);
  }, [isOpenModal, isView, planType, plans, setFieldIfChanged]);

  // Enterprise gets back its negotiated prices and limit (edit) or a blank slate (create) when the
  // plan type lands on Enterprise, so a stale system value is never submitted. Deliberately NOT
  // keyed on `plans`: the basic-plans query resolving mid-edit must not clobber a value the admin
  // has already typed into the unlocked Enterprise inputs.
  useEffect(() => {
    if (!isOpenModal || isView || planType !== SubscriptionType.ENTERPRISE) return;

    if (offer && mode === 'edit') {
      setFieldIfChanged(
        'monthlyPrice',
        offer.monthlyPriceInCents != null ? offer.monthlyPriceInCents / 100 : undefined,
      );
      setFieldIfChanged('yearlyPrice', offer.yearlyPriceInCents / 100);
      setFieldIfChanged('stakeholderLimit', offer.stakeholdersLimit);
    } else if (mode === 'create') {
      setFieldIfChanged('monthlyPrice', undefined);
      setFieldIfChanged('yearlyPrice', undefined);
      setFieldIfChanged('stakeholderLimit', undefined);
    }
  }, [isOpenModal, isView, planType, offer, mode, setFieldIfChanged]);

  const handleCloseModal = useCallback(() => {
    handleClose();
    reset(CREATE_DEFAULT_VALUES);
  }, [handleClose, reset]);

  const submitCreate = useCallback(
    (sendEmail: boolean) =>
      handleSubmit(async (data: CreateOfferFormSchema) => {
        try {
          const result = await createSubscriptionOfferAsync({
            companyName: data.companyName.trim(),
            companyDomain: data.companyDomain.trim().toLowerCase(),
            planType: data.planType,
            // superRefine rejects a missing limit for every plan type, so it is present here.
            stakeholdersLimit: data.stakeholderLimit!,
            docusignEnvelopeLimit: data.docusignEnabled
              ? (data.docusignEnvelopeLimit ?? null)
              : null,
            recipients: parseRecipients(data.recipients),
            isMonthlyEnabled: isEnterprise ? !!data.monthlyEnabled : true,
            monthlyPriceInCents:
              isEnterprise && data.monthlyEnabled && data.monthlyPrice
                ? Math.round(data.monthlyPrice * 100)
                : null,
            yearlyPriceInCents:
              isEnterprise && data.yearlyPrice ? Math.round(data.yearlyPrice * 100) : null,
            promoCode: data.promoCode?.trim() || null,
            sendEmail,
          });

          await navigator.clipboard.writeText(result.link).catch(() => undefined);

          toast.success(
            sendEmail ? 'Link sent and copied to clipboard' : 'Link copied to clipboard',
          );
          invalidateQuery();
          handleCloseModal();
        } catch (error) {
          if (isInvalidPromoCodeError(error)) {
            setError('promoCode', { type: 'server', message: INVALID_PROMO_CODE_MESSAGE });
          }
        }
      })(),
    [
      handleSubmit,
      createSubscriptionOfferAsync,
      isEnterprise,
      invalidateQuery,
      handleCloseModal,
      setError,
    ],
  );

  const submitUpdate = useCallback(
    () =>
      handleSubmit(async (data: CreateOfferFormSchema) => {
        if (!offer) return;

        try {
          await updateSubscriptionOfferAsync({
            id: offer.id,
            data: {
              companyName: data.companyName.trim(),
              companyDomain: data.companyDomain.trim().toLowerCase(),
              planType: data.planType,
              // superRefine rejects a missing limit for every plan type, so it is present here.
              stakeholdersLimit: data.stakeholderLimit!,
              docusignEnvelopeLimit: data.docusignEnabled
                ? (data.docusignEnvelopeLimit ?? null)
                : null,
              recipients: parseRecipients(data.recipients),
              isMonthlyEnabled: isEnterprise ? !!data.monthlyEnabled : true,
              monthlyPriceInCents:
                isEnterprise && data.monthlyEnabled && data.monthlyPrice
                  ? Math.round(data.monthlyPrice * 100)
                  : null,
              yearlyPriceInCents:
                isEnterprise && data.yearlyPrice ? Math.round(data.yearlyPrice * 100) : null,
              promoCode: data.promoCode?.trim() || null,
            },
          });

          toast.success('Subscription offer updated');
          invalidateQuery();
          handleCloseModal();
        } catch (error) {
          if (isInvalidPromoCodeError(error)) {
            setError('promoCode', { type: 'server', message: INVALID_PROMO_CODE_MESSAGE });
          }
        }
      })(),
    [
      handleSubmit,
      updateSubscriptionOfferAsync,
      offer,
      isEnterprise,
      invalidateQuery,
      handleCloseModal,
      setError,
    ],
  );

  return (
    <Sheet open={isOpenModal}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleCloseModal}
        side="RIGHT"
      >
        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="truncate text-xl font-semibold text-gray-700">
              {MODAL_TITLE[mode]}
            </span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-xl border-b border-gray-300 bg-white">
            <fieldset
              className="flex h-full min-h-0 flex-col gap-2 overflow-y-auto px-6 py-2"
              disabled={isView}
            >
              <FormInput
                control={control}
                disabled={isView}
                name="companyName"
                placeholder="Company Name"
                wrapperClassName="w-full"
              />
              <div className="h-px w-full bg-gray-100" />
              <FormInput
                control={control}
                disabled={isView}
                name="companyDomain"
                placeholder="Company Domain"
                wrapperClassName="w-full"
              />
              <div className="h-px w-full bg-gray-100" />

              <FormCombobox className="w-full" control={control} name="planType">
                <div className="relative">
                  <FormComboboxInput
                    control={control}
                    customValue={(value) =>
                      value && subscriptionTypeTitle[value as SubscriptionType]
                    }
                    disabled={isView}
                    icon={!isView && <ChevronDownIcon />}
                    name="planType"
                    placeholder="Plan"
                    readOnly
                  />
                  <ComboboxButton className="absolute left-0 top-0 z-20 h-full w-full" />
                </div>
                <ComboboxOptions className="z-20 w-[418px] overflow-x-hidden">
                  {Object.entries(subscriptionTypeTitle).map(([key, value]) => (
                    <FormComboboxOption
                      className="w-full"
                      control={control}
                      key={key}
                      name="planType"
                      value={key}
                    >
                      <span className="text-sm text-gray-700">{value}</span>
                    </FormComboboxOption>
                  ))}
                </ComboboxOptions>
              </FormCombobox>

              {/* Frequency and Price */}
              <div className="flex w-full flex-col items-center gap-3 rounded-md bg-gray-50 px-[2px] pb-[2px] pt-3">
                <div className="flex w-full items-start px-4">
                  <span className="text-sm font-[550] text-gray-700">Frequency and Price</span>
                </div>
                <div className="flex w-full flex-col gap-3 rounded-md bg-white py-3 shadow-sm">
                  <div className="flex w-full items-center justify-center gap-4 px-4">
                    <div className="flex flex-1 items-center gap-2">
                      {isEnterprise ? (
                        <FormCheckbox control={control} disabled={isView} name="monthlyEnabled" />
                      ) : (
                        <Checkbox checked disabled />
                      )}
                      <span className="text-sm text-gray-700">Monthly</span>
                    </div>
                    <FormInput
                      control={control}
                      disabled={isView || !isEnterprise || !monthlyEnabled}
                      name="monthlyPrice"
                      numberOnly
                      placeholder="Monthly price"
                      shouldFormatNumber
                      wrapperClassName="w-[192px]"
                    />
                  </div>
                  <div className="h-px w-full bg-gray-200" />
                  <div className="flex w-full items-center justify-center gap-4 px-4">
                    <div className="flex flex-1 items-center gap-2">
                      <Checkbox checked disabled />
                      <span className="text-sm text-gray-700">Annually</span>
                    </div>
                    <FormInput
                      control={control}
                      disabled={isView || !isEnterprise}
                      name="yearlyPrice"
                      numberOnly
                      placeholder="Annually price"
                      shouldFormatNumber
                      wrapperClassName="w-[192px]"
                    />
                  </div>
                </div>
              </div>

              <FormInput
                control={control}
                disabled
                name="currency"
                placeholder="Currency"
                wrapperClassName="w-full"
              />

              {/* Standard plans carry the catalog's preset limit — only Enterprise is editable */}
              <FormInput
                control={control}
                disabled={isView || !isEnterprise}
                name="stakeholderLimit"
                numberOnly
                placeholder="Stakeholder limit"
                wrapperClassName="w-full"
              />

              <FormInput
                control={control}
                disabled={isView}
                name="promoCode"
                onChange={() => clearErrors('promoCode')}
                placeholder="Promocode"
                wrapperClassName="w-full"
              />

              <FormInput
                control={control}
                disabled={isView}
                name="recipients"
                placeholder="Recipients (separate with comma)"
                wrapperClassName="w-full"
              />

              {/* DocuSign Envelopes — view mode shows a read-only limit field per design */}
              {isView ? (
                docusignEnabled && (
                  <FormInput
                    control={control}
                    disabled
                    name="docusignEnvelopeLimit"
                    placeholder="DocuSign Limit"
                    shouldFormatNumber
                    wrapperClassName="w-full"
                  />
                )
              ) : (
                <div className="flex w-full flex-col items-center gap-3 rounded-md border border-gray-100 bg-white py-3">
                  <div className="flex w-full items-center gap-2 px-3">
                    <Switch
                      checked={docusignEnabled}
                      onChange={(value) =>
                        setValue('docusignEnabled', value, { shouldValidate: true })
                      }
                    />
                    <span className="text-sm text-gray-700">DocuSign Envelopes</span>
                  </div>
                  {docusignEnabled && (
                    <div className="flex w-full flex-col gap-1 px-4">
                      <FormInput
                        control={control}
                        name="docusignEnvelopeLimit"
                        numberOnly
                        placeholder="Limit"
                        wrapperClassName="w-full"
                      />
                      {planType && ENVELOPE_HINTS[planType as SubscriptionType] && (
                        <span className="text-xs text-gray-400">
                          {ENVELOPE_HINTS[planType as SubscriptionType]}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </fieldset>

            {isView && (
              <div className="w-full px-6 pb-4 pt-2">
                <Button
                  className="h-9 w-full rounded border border-gray-300 bg-white text-sm font-[450] text-gray-700"
                  onClick={onSwitchToEdit}
                  styleType="NONE"
                  type="button"
                >
                  Edit Subscription
                </Button>
              </div>
            )}
          </div>

          <div
            className={twMerge(
              'mt-4 flex w-full items-center gap-3 px-6 py-1',
              isView ? 'justify-center' : 'justify-between',
            )}
          >
            <Button
              className={twMerge(
                'w-fit bg-transparent px-3 py-[6px] text-sm font-[450] text-gray-700',
                isView && 'h-9 rounded border border-gray-100',
              )}
              onClick={handleCloseModal}
              styleType="NONE"
              type="button"
            >
              Cancel
            </Button>

            {isEdit && (
              <Button
                className="h-9 w-fit bg-brand-700 px-4 py-[10px] text-sm font-[550] text-white"
                disabled={isPending}
                isLoading={isPending}
                onClick={submitUpdate}
                type="button"
              >
                Save changes
              </Button>
            )}

            {mode === 'create' && (
              <div className="flex gap-2">
                <Button
                  className={twMerge(
                    'h-9 w-fit border border-gray-100 bg-white px-4 py-[10px] text-sm font-[550] text-gray-700',
                    !isFormValid && 'cursor-not-allowed opacity-50',
                  )}
                  disabled={isPending || !isFormValid}
                  isLoading={isCreating}
                  onClick={() => submitCreate(false)}
                  styleType="NONE"
                  type="button"
                >
                  Create and Copy
                </Button>
                <Button
                  className={twMerge(
                    'h-9 w-fit bg-brand-700 px-4 py-[10px] text-sm font-[550] text-white',
                    (!hasRecipient || !isFormValid) && 'cursor-not-allowed opacity-50',
                  )}
                  disabled={isPending || !isFormValid || !hasRecipient}
                  isLoading={isCreating}
                  onClick={() => submitCreate(true)}
                  type="button"
                >
                  Create and Send Link
                </Button>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
